import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { STAGES } from './PipelineTracker';

const ApplicantTable = ({
  applicants,
  onApplicantClick,
  onBulkAction,
  selectedApplicants,
  onSelectionChange,
  onCallApplicant,
  onScheduleInterview,
  onMoreActions,
  onDeleteApplicant
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'applicationDate', direction: 'desc' });
  const [openMenuId, setOpenMenuId] = useState(null);

  // Color coding by stage index range, matching PipelineTracker's stage list
  const getStageColor = (stageKey) => {
    const norm = (stageKey === 'ai_screening' || stageKey === 'hr_screening') ? 'screening' : stageKey;
    const index = STAGES.findIndex((s) => s.key === norm);
    if (index <= 1) return 'bg-blue-100 text-blue-800';       // Applied, Resume Review
    if (index === 2) return 'bg-yellow-100 text-yellow-800';    // AI & HR Screening
    if (index === 3) return 'bg-indigo-100 text-indigo-800';    // Shortlisted
    if (index <= 5) return 'bg-purple-100 text-purple-800';    // Technical, Manager Interview
    if (index === 6) return 'bg-indigo-100 text-indigo-800';   // Final HR
    if (index <= 8) return 'bg-green-100 text-green-800';      // Offer Accepted, BG Verification
    if (index === 9) return 'bg-emerald-100 text-emerald-800'; // Joined
    return 'bg-gray-100 text-gray-800';
  };

  const getAtsScoreBadge = (score) => {
    if (score === null || score === undefined) return null;
    const numScore = Math.round(Number(score));
    const color =
      numScore >= 80 ? 'bg-green-100 text-green-700 border-green-300' :
      numScore >= 60 ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
      'bg-red-100 text-red-700 border-red-300';
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold border ${color}`} title="ATS Match Score">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
        </svg>
        {numScore}%
      </span>
    );
  };

  const getDuplicateBadge = (applicant) => {
    if (!applicant?.is_duplicate && applicant?.is_duplicate !== 1 && applicant?.is_duplicate !== '1') return null;
    const reasonText =
      applicant.duplicate_reason === 'both' ? 'Matched by Email & Phone' :
      applicant.duplicate_reason === 'phone' ? 'Matched by Phone' :
      'Matched by Email';

    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-700"
        title={`Duplicate Application (${reasonText})`}
      >
        <Icon name="Copy" size={10} />
        Duplicate
      </span>
    );
  };

  const getSourceBadge = (applicant) => {
    if (applicant?.application_source === 'walkin_drive') {
      return (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-purple-100 text-purple-800 border border-purple-300 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-700"
          title="Candidate sourced & selected from Walk-in Hiring Drive"
        >
          <Icon name="QrCode" size={10} />
          Walk-in Drive
        </span>
      );
    }
    return null;
  };

  const getStatusBadge = (applicant) => {
    // Rejected/on_hold/dropped/withdrawn takes priority if present
    if (applicant.activeFlag) {
      const flagLabels = {
        rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
        on_hold: { color: 'bg-orange-100 text-orange-800', label: 'On Hold' },
        dropped: { color: 'bg-gray-100 text-gray-800', label: 'Dropped' },
        withdrawn: { color: 'bg-gray-100 text-gray-600', label: 'Withdrawn' }
      };
      const flagConfig = flagLabels[applicant.activeFlag.flag_type];
      if (flagConfig) {
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${flagConfig.color}`}>
            {flagConfig.label}
          </span>
        );
      }
    }

    const rawStageKey = applicant.pipeline_stage || 'applied';
    const normStageKey = (rawStageKey === 'ai_screening' || rawStageKey === 'hr_screening') ? 'screening' : rawStageKey;
    const stage = STAGES.find((s) => s.key === normStageKey || s.key === rawStageKey);
    const label = stage ? stage.label : (rawStageKey === 'ai_screening' || rawStageKey === 'hr_screening' ? 'AI & HR Screening' : 'Applied');
    const color = getStageColor(rawStageKey);

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${color}`}>
        {label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      high: { color: 'bg-red-100 text-red-800', icon: 'AlertTriangle' },
      medium: { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
      low: { color: 'bg-gray-100 text-gray-800', icon: 'Minus' }
    };

    const config = priorityConfig[priority] || priorityConfig.medium;
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon name={config.icon} size={12} />
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </div>
    );
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      onSelectionChange(applicants.map(applicant => applicant.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectApplicant = (applicantId, e) => {
    e.stopPropagation();
    if (selectedApplicants.includes(applicantId)) {
      onSelectionChange(selectedApplicants.filter(id => id !== applicantId));
    } else {
      onSelectionChange([...selectedApplicants, applicantId]);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const sortedApplicants = [...applicants].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === bValue) {
      const aTime = a.created_at || a.stage_updated_at || a.applicationDate || '';
      const bTime = b.created_at || b.stage_updated_at || b.applicationDate || '';
      return sortConfig.direction === 'asc'
        ? (aTime > bTime ? 1 : -1)
        : (aTime < bTime ? 1 : -1);
    }

    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Bulk Actions Bar */}
      {selectedApplicants.length > 0 && (
        <div className="bg-primary/10 border-b border-border p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <p className="text-sm font-medium text-foreground">
                {selectedApplicants.length} applicant(s) selected
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBulkAction('schedule-interview')}
                  iconName="Calendar"
                  iconPosition="left"
                >
                  Schedule Interview
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBulkAction('send-reminder')}
                  iconName="Bell"
                  iconPosition="left"
                >
                  Send Reminder
                </Button>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSelectionChange([])}
              iconName="X"
            >
              Clear Selection
            </Button>
          </div>
        </div>
      )}

      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 p-4">
                <input
                  type="checkbox"
                  checked={selectedApplicants.length === applicants.length && applicants.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-input"
                />
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 hover:text-primary"
                >
                  Applicant
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('position')}
                  className="flex items-center gap-2 hover:text-primary"
                >
                  Position
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('applicationDate')}
                  className="flex items-center gap-2 hover:text-primary"
                >
                  Applied Date
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">HR Assigned</th>
              <th className="text-left p-4 font-medium text-foreground">Status</th>
              <th className="text-left p-4 font-medium text-foreground">Priority</th>
              <th className="text-left p-4 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedApplicants.map((applicant) => (
              <tr
                key={applicant.id}
                className="border-b border-border hover:bg-muted/30 cursor-pointer transition-colors"
                onClick={() => onApplicantClick(applicant)}
              >
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedApplicants.includes(applicant.id)}
                    onChange={(e) => handleSelectApplicant(applicant.id, e)}
                    className="rounded border-input"
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <Image
                      src={applicant.avatar}
                      alt={applicant.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-foreground">{applicant.name}</p>
                        {getAtsScoreBadge(applicant.ats_score)}
                        {getDuplicateBadge(applicant)}
                        {getSourceBadge(applicant)}
                      </div>
                      <p className="text-sm text-muted-foreground">{applicant.email}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <p className="font-medium text-foreground">{applicant.position}</p>
                  <p className="text-sm text-muted-foreground">{applicant.experience} experience</p>
                </td>
                <td className="p-4">
                  <p className="text-foreground">{formatDate(applicant.applicationDate)}</p>
                </td>
                <td className="p-4">
                  {applicant.hrAssigned ? (
                    <div className="flex items-center gap-2">
                      <Image
                        src={applicant.hrAssigned.avatar}
                        alt={applicant.hrAssigned.name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-sm text-foreground">{applicant.hrAssigned.name}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">Unassigned</span>
                  )}
                </td>
                <td className="p-4">
                  {getStatusBadge(applicant)}
                </td>
                <td className="p-4">
                  {getPriorityBadge(applicant.priority)}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onCallApplicant) {
                          onCallApplicant(applicant);
                        }
                      }}
                      title="Log Call / View Interactions"
                    >
                      <Icon name="Phone" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onScheduleInterview) {
                          onScheduleInterview(applicant);
                        }
                      }}
                      title="Schedule Interview"
                    >
                      <Icon name="Calendar" size={16} />
                    </Button>
                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === applicant.id ? null : applicant.id);
                        }}
                        title="More Actions"
                      >
                        <Icon name="MoreHorizontal" size={16} />
                      </Button>
                      {openMenuId === applicant.id && (
                        <div
                          className="absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              if (onMoreActions) onMoreActions(applicant);
                              else onApplicantClick(applicant);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2"
                          >
                            <Icon name="Eye" size={14} />
                            View Details
                          </button>
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              if (onDeleteApplicant) onDeleteApplicant(applicant);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Icon name="Trash2" size={14} />
                            Delete Application
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden">
        {sortedApplicants.map((applicant) => (
          <div
            key={applicant.id}
            className="border-b border-border p-4 hover:bg-muted/30 cursor-pointer transition-colors"
            onClick={() => onApplicantClick(applicant)}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selectedApplicants.includes(applicant.id)}
                onChange={(e) => handleSelectApplicant(applicant.id, e)}
                className="mt-1 rounded border-input"
              />
              <Image
                src={applicant.avatar}
                alt={applicant.name}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-foreground truncate">{applicant.name}</h4>
                      {getAtsScoreBadge(applicant.ats_score)}
                      {getDuplicateBadge(applicant)}
                      {getSourceBadge(applicant)}
                    </div>
                    <p className="text-sm text-muted-foreground">{applicant.position}</p>
                  </div>
                  {getStatusBadge(applicant)}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>Applied: {formatDate(applicant.applicationDate)}</span>
                    {getPriorityBadge(applicant.priority)}
                  </div>

                  {applicant.hrAssigned && (
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="User" size={14} />
                      <span>HR: {applicant.hrAssigned.name}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Phone"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onCallApplicant) {
                        onCallApplicant(applicant);
                      }
                    }}
                  >
                    Call
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Calendar"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onScheduleInterview) {
                        onScheduleInterview(applicant);
                      }
                    }}
                  >
                    Schedule
                  </Button>
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === applicant.id ? null : applicant.id);
                      }}
                    >
                      <Icon name="MoreHorizontal" size={16} />
                    </Button>
                    {openMenuId === applicant.id && (
                      <div
                        className="absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => {
                            setOpenMenuId(null);
                            if (onMoreActions) onMoreActions(applicant);
                            else onApplicantClick(applicant);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2"
                        >
                          <Icon name="Eye" size={14} />
                          View Details
                        </button>
                        <button
                          onClick={() => {
                            setOpenMenuId(null);
                            if (onDeleteApplicant) onDeleteApplicant(applicant);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Icon name="Trash2" size={14} />
                          Delete Application
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {applicants.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No applicants found</h3>
          <p className="text-muted-foreground">Try adjusting your filters or search criteria</p>
        </div>
      )}
    </div>
  );
};

export default ApplicantTable;